import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BookOpen,
  Boxes,
  Film,
  FolderKanban,
  LayoutGrid,
  LogOut,
  Menu,
  MoreHorizontal,
  Settings,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { CreditIndicator } from "@/components/CreditIndicator";
import { ProjectSelector } from "@/components/ProjectSelector";
import { TeamSelector } from "@/components/TeamSelector";
import { ThemeQuickSwitcher } from "@/components/theme/ThemeQuickSwitcher";
import { BrandMark } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/", icon: Film, label: "Studio" },
  { to: "/gallery", icon: LayoutGrid, label: "Gallery" },
  { to: "/library", icon: BookOpen, label: "Library" },
  { to: "/elements", icon: Boxes, label: "Elements" },
  { to: "/projects", icon: FolderKanban, label: "Projects" },
  { to: "/settings", icon: SlidersHorizontal, label: "Settings" },
] as const;

const primaryMobileItems = navItems.slice(0, 4);

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn("flex items-center gap-2", !compact && "px-5 py-5")}>
      <BrandMark className="h-8 w-8" />
      <span className="truncate font-display text-sm font-bold tracking-tight">PandaFilms</span>
    </div>
  );
}

function Navigation({ isAdmin, onNavigate }: { isAdmin: boolean; onNavigate?: () => void }) {
  return (
    <nav className="flex-1 px-3 py-2" aria-label="Main navigation">
      {navItems.map(({ to, icon: Icon, label }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
              isActive
                ? "bg-studio-accent-soft font-semibold text-studio-accent"
                : "text-studio-muted hover:bg-studio-card hover:text-studio-ink"
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink
          to="/admin"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "mt-2 flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
              isActive
                ? "bg-studio-accent-soft font-semibold text-studio-accent"
                : "text-studio-muted hover:bg-studio-card hover:text-studio-ink"
            )
          }
        >
          <Settings className="h-4 w-4 shrink-0" />
          Admin
        </NavLink>
      )}
    </nav>
  );
}

function AccountPanel({ onLogout, onNavigate }: { onLogout: () => void; onNavigate?: () => void }) {
  const user = useAuthStore((state) => state.user);
  const activeTeam = useAuthStore((state) => state.activeTeam);
  return (
    <div className="border-t border-studio-border p-3">
      <CreditIndicator />
      <ThemeQuickSwitcher onNavigate={onNavigate} />
      <div className="mt-3 flex items-center justify-between gap-3 px-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-studio-ink">{user?.email}</p>
          <p className="text-[10px] capitalize text-studio-muted">{user?.is_superuser ? "Super admin" : activeTeam?.role.replace(/_/g, " ") ?? "User"}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
          title="Logout"
          aria-label="Logout"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function StudioLayout() {
  const { user, logout } = useAuthStore();
  const activeTeamId = useAuthStore((state) => state.activeTeam?.id);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = Boolean(user?.is_superuser);
  const secondaryActive = ["/projects", "/settings", "/admin"].some((path) =>
    location.pathname.startsWith(path)
  );

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuOpen]);

  function handleLogout() {
    setMobileMenuOpen(false);
    logout();
  }

  return (
    <div className="flex min-h-dvh bg-studio-bg lg:h-screen lg:overflow-hidden">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-studio-border bg-studio-panel lg:flex">
        <Brand />
        <div className="space-y-2 px-3 pb-2">
          <TeamSelector />
          <ProjectSelector />
        </div>
        <Navigation isAdmin={isAdmin} />
        <AccountPanel onLogout={handleLogout} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="mobile-safe-top sticky top-0 z-30 flex min-h-[calc(3.5rem+env(safe-area-inset-top))] shrink-0 items-center justify-between border-b border-studio-border bg-studio-panel/95 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open navigation"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            className="-ml-2 flex h-11 w-11 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Brand compact />
          <ThemeQuickSwitcher compact />
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet key={activeTeamId ?? "no-active-team"} />
        </main>
      </div>

      <nav
        aria-label="Mobile primary navigation"
        className="mobile-safe-bottom fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-studio-border bg-studio-panel/95 px-1 backdrop-blur lg:hidden"
      >
        {primaryMobileItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              cn(
                "flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg px-1 text-[10px] font-medium transition-colors",
                isActive ? "text-studio-accent" : "text-studio-muted active:bg-studio-card"
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className={cn(
            "flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg px-1 text-[10px] font-medium transition-colors active:bg-studio-card",
            secondaryActive ? "text-studio-accent" : "text-studio-muted"
          )}
          aria-label="More navigation"
        >
          <MoreHorizontal className="h-[18px] w-[18px]" />
          <span>More</span>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label="Close navigation"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="mobile-safe-top mobile-safe-bottom absolute inset-y-0 left-0 flex w-[min(86vw,20rem)] flex-col border-r border-studio-border bg-studio-panel shadow-panel animate-rise"
          >
            <div className="flex items-center justify-between px-4 py-3">
              <Brand compact />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation"
                className="flex h-11 w-11 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2 px-3 pb-2">
              <TeamSelector />
              <ProjectSelector />
            </div>
            <Navigation isAdmin={isAdmin} onNavigate={() => setMobileMenuOpen(false)} />
            <AccountPanel onLogout={handleLogout} onNavigate={() => setMobileMenuOpen(false)} />
          </aside>
        </div>
      )}
    </div>
  );
}
